import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface TextLevelerCardPdfProps {
  content: string;
  level: string;
  imageUrl?: string;
  customizationSettings: PdfCustomizationSettings;
  author?: string;
  createdAt?: string;
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#eff6ff", // blue-50
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
    backgroundColor: "#dbeafe", // blue-100
    padding: 8,
    borderRadius: 6,
  },
  title: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#2563eb", // blue-600
  },
  levelContainer: {
    backgroundColor: "#bfdbfe", // blue-200
    padding: 6,
    borderRadius: 4,
    marginBottom: 8,
  },
  levelText: {
    fontSize: 11,
    color: "#1e40af", // blue-800
  },
  content: {
    ...commonStyles.sectionContent,
    // backgroundColor: "#ffffff",
    padding: 10,
    borderRadius: 6,
    marginTop: 8,
  },
});

const TextLevelerDocument = ({
  content,
  level,
  imageUrl = "",
  customizationSettings,
  author = "",
  createdAt = "",
}: TextLevelerCardPdfProps) => {
  const { t } = useTranslation();

  const getLevelLabel = (level: string) => {
    const levelMap: { [key: string]: string } = {
      "1": t("textLeveler.levels.elementary"),
      "3": t("textLeveler.levels.intermediate"),
      "5": t("textLeveler.levels.upperIntermediate"),
      "7": t("textLeveler.levels.advanced"),
      "9": t("textLeveler.levels.proficient"),
    };
    return levelMap[level] || level;
  };

  return (
    <PdfLayout
      imageUrl={imageUrl}
      headerTextColor="#2563eb"
      customizationSettings={customizationSettings}
      author={author}
      createdDate={createdAt}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <Text
            style={[
              styles.title,
              {
                fontSize: scaleFontSize(
                  styles.title.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            ]}
          >
            {t("textLeveler.details")}
          </Text>
        </View>

        <View style={styles.levelContainer}>
          <Text
            style={[
              styles.levelText,
              {
                fontSize: scaleFontSize(
                  styles.levelText.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            ]}
          >
            {t("textLeveler.level")}: {getLevelLabel(level)}
          </Text>
        </View>

        <MarkdownRenderer
          customStyle={{
            h3: {
              text: {
                ...styles.content,
                fontSize: scaleFontSize(
                  styles.content.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            },
            blockquote: {
              text: {
                ...styles.content,
                fontSize: scaleFontSize(
                  styles.content.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            },
            listItem: {
              text: {
                ...styles.content,
                fontSize: scaleFontSize(
                  styles.content.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            },

            h2: {
              text: {
                ...styles.content,
                fontSize: scaleFontSize(
                  styles.content.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            },
            text: {
              text: {
                ...styles.content,
                fontSize: scaleFontSize(
                  styles.content.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            },
            h1: {
              text: {
                ...styles.content,
                fontSize: scaleFontSize(
                  styles.content.fontSize,
                  customizationSettings.fontSize,
                ),
              },
            },
          }}
          content={content}
        />
      </View>
    </PdfLayout>
  );
};

function TextLevelerCardPDF(props: TextLevelerCardPdfProps) {
  return <TextLevelerDocument {...props} />;
}

export default TextLevelerCardPDF;
